import { Suspense, useEffect, useMemo, useState } from "react";
import {
    Alert,
    Autocomplete,
    Box,
    CircularProgress,
    Stack,
    TextField,
    Typography as MuiTypography,
    useTheme,
} from "@mui/material";
import { Typography as FoodsTypography } from "@foodpilot/foods";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router";

import {
    TransportCircuit,
    TransportCircuitWrite,
    useTransportCircuits,
    useUpsertTransportCircuit,
} from "src/api/client-api/transport/useTransportCircuits";
import { isTransportMutationContractError } from "src/api/client-api/transport/TransportMutationContractError";
import { TransportLeg, useTransportLegs } from "src/api/client-api/transport/useTransportLegs";
import { useSearchTransportReferences } from "src/api/sure-api/useSearchTransportReferences";
import { LazyTransportCircuitMap, LazyTransportEmptyMapPreview } from "src/components/Transport/Map";
import {
    EntityFormPageState,
    FoodsLoadingIndicator,
    FoodsSelectField,
    FoodsTextField,
} from "src/components/EntityForm";
import { RemoteErrorAlert } from "src/components/PageState/RemoteErrorAlert";
import { useFoodpilotContext } from "src/context/FoodpilotContext";
import { URLHandler } from "src/services/URLHandler";
import { useSearchTransportModes } from "src/api/sure-api/useSearchTransportModes";
import { useSnackbar } from "src/utils/useSnackbar";
import { TransportAddLegModal } from "./TransportAddLegModal";
import { TransportCircuitLegsSection } from "./TransportCircuitLegsSection";
import {
    TransportCreatePageShell,
    TransportCreateSection,
    TransportFormMapLayout,
    TransportFormMapPanel,
    TransportFormTabPanel,
    TransportFormTabs,
    transportCreateTextFieldSx,
} from "./TransportCreatePageShell";
import { TransportScoresPanel } from "./TransportScoresPanel";
import { useTransportAppearance } from "./transportAppearance";

const transportMapPreviewAspectRatio = "334 / 235";

type LocalDraft = {
    name: string;
    legIds: number[];
};

const blankDraft: LocalDraft = {
    name: "",
    legIds: [],
};

const getLegIri = (leg: TransportLeg) => (leg["@id"] ?? `/api/transport-legs/${leg.id}`) as IRI;
const getTransportLegId = (transportLeg: TransportCircuit["circuitLegs"][number]["transportLeg"]) => {
    if (typeof transportLeg !== "string") {
        return transportLeg.id;
    }

    const match = transportLeg.match(/\/api\/transport-legs\/(\d+)$/);
    return match ? Number.parseInt(match[1], 10) : null;
};

export const TransportCircuitCreatePage = () => {
    const { t } = useTranslation();
    const theme = useTheme();
    const navigate = useNavigate();
    const { setSnackbarProps } = useSnackbar();
    const appearance = useTransportAppearance("product-v2");
    const { circuitId } = useParams<{ circuitId?: string }>();
    const { selectedCompany } = useFoodpilotContext();
    const organizationId = selectedCompany?.id;
    const editingCircuitId = circuitId !== undefined ? Number.parseInt(circuitId, 10) : undefined;
    const isEditMode = editingCircuitId !== undefined && !Number.isNaN(editingCircuitId);
    const upsertCircuit = useUpsertTransportCircuit();
    const {
        transportCircuits: loadedTransportCircuits,
        isError: isEditingCircuitError,
        isLoading: isEditingCircuitLoading,
    } = useTransportCircuits({
        enabled: organizationId !== undefined && isEditMode,
        organizationId,
    });
    const transportCircuits = useMemo(() => loadedTransportCircuits ?? [], [loadedTransportCircuits]);
    const editingCircuit = useMemo(
        () => transportCircuits.find((circuit) => circuit.id === editingCircuitId) ?? null,
        [editingCircuitId, transportCircuits],
    );
    const isEditingCircuitNotFound =
        isEditMode && !isEditingCircuitLoading && !isEditingCircuitError && editingCircuit === null;
    const {
        transportLegs: loadedTransportLegs,
        isLoading: isTransportLegsLoading,
        isError: isTransportLegsError,
    } = useTransportLegs({ organizationId, enabled: organizationId !== undefined });
    const transportLegs = useMemo(() => loadedTransportLegs ?? [], [loadedTransportLegs]);
    const { transportReferences } = useSearchTransportReferences({ enabled: organizationId !== undefined });
    const { transportModes } = useSearchTransportModes({ enabled: organizationId !== undefined });
    const [draft, setDraft] = useState<LocalDraft>(blankDraft);
    const [linkedRefId, setLinkedRefId] = useState<number | null>(null);
    const [showAddLeg, setShowAddLeg] = useState(false);
    const [selectedTab, setSelectedTab] = useState<"form" | "scores">("form");
    const isEditingLinked = editingCircuit?.linkMode === "LINKED";
    const isReferenceLocked = linkedRefId !== null || isEditingLinked;
    const creationIdentitySectionTotal = 2;
    const editSectionTotal = isReferenceLocked ? 1 : 2;
    const isTransportLegCatalogUnavailable = isTransportLegsLoading || isTransportLegsError;

    useEffect(() => {
        if (isTransportLegCatalogUnavailable) {
            setShowAddLeg(false);
        }
    }, [isTransportLegCatalogUnavailable]);

    useEffect(() => {
        if (!editingCircuit) return;

        const legIds: number[] = [];
        for (const circuitLeg of editingCircuit.circuitLegs.toSorted((a, b) => a.orderIndex - b.orderIndex)) {
            const legId = getTransportLegId(circuitLeg.transportLeg);
            if (legId !== null) {
                legIds.push(legId);
            }
        }

        setDraft({
            name: editingCircuit.name ?? "",
            legIds,
        });
        setLinkedRefId(null);
    }, [editingCircuit]);

    const selectedLegEntries = useMemo(() => {
        const embeddedEditingLegs = (editingCircuit?.circuitLegs ?? []).flatMap((circuitLeg) =>
            typeof circuitLeg.transportLeg === "string" ? [] : [circuitLeg.transportLeg],
        );
        const legsById = new Map([...embeddedEditingLegs, ...transportLegs].map((leg) => [leg.id, leg]));

        return draft.legIds.map((legId, draftIndex) => ({
            draftIndex,
            leg: legsById.get(legId) ?? null,
            legId,
        }));
    }, [draft.legIds, editingCircuit?.circuitLegs, transportLegs]);
    const selectedLegs = useMemo(
        () => selectedLegEntries.flatMap(({ leg }) => (leg === null ? [] : [leg])),
        [selectedLegEntries],
    );
    const areAllSelectedLegsResolved = selectedLegEntries.every(({ leg }) => leg !== null);

    const previewCircuit: TransportCircuit | null = useMemo(() => {
        if (isEditingLinked && editingCircuit) return editingCircuit;
        if (selectedLegs.length === 0) return null;

        return {
            id: editingCircuit?.id ?? -1,
            linkMode: editingCircuit?.linkMode ?? "LOCAL",
            sourceReferenceCircuitId: editingCircuit?.sourceReferenceCircuitId ?? linkedRefId,
            name: draft.name || null,
            circuitLegs: selectedLegs.map((leg, orderIndex) => ({
                id: -orderIndex - 1,
                orderIndex,
                transportLeg: leg,
            })),
        };
    }, [draft.name, editingCircuit, isEditingLinked, linkedRefId, selectedLegs]);

    if (organizationId === undefined) {
        if (appearance === "legacy") {
            return <MuiTypography variant="body2">{t("transport.no_org", "No organization selected")}</MuiTypography>;
        }

        return (
            <EntityFormPageState>
                <FoodsTypography role="status" variant="body2">
                    {t("transport.no_org", "No organization selected")}
                </FoodsTypography>
            </EntityFormPageState>
        );
    }

    if (isEditMode && isEditingCircuitLoading && editingCircuit === null) {
        if (appearance === "legacy") {
            return (
                <Stack alignItems="center" padding={4}>
                    <CircularProgress />
                </Stack>
            );
        }

        return (
            <EntityFormPageState>
                <Stack alignItems="center" padding={4}>
                    <FoodsLoadingIndicator label={t("common.loading", "Loading...")} />
                </Stack>
            </EntityFormPageState>
        );
    }

    if (isEditMode && isEditingCircuitNotFound) {
        if (appearance === "legacy") {
            return (
                <MuiTypography variant="body2">
                    {t("transport.circuits.not_found", "Transport itinerary not found")}
                </MuiTypography>
            );
        }

        return (
            <EntityFormPageState>
                <FoodsTypography role="status" variant="body2">
                    {t("transport.circuits.not_found", "Transport itinerary not found")}
                </FoodsTypography>
            </EntityFormPageState>
        );
    }

    if (isEditMode && isEditingCircuitError && editingCircuit === null) {
        if (appearance === "legacy") {
            return (
                <Alert severity="error">
                    {t("transport.circuits.load_error", "Unable to load transport itinerary")}
                </Alert>
            );
        }

        return (
            <EntityFormPageState>
                <RemoteErrorAlert
                    message={t("transport.circuits.load_error", "Unable to load transport itinerary")}
                    status="server-error"
                />
            </EntityFormPageState>
        );
    }

    const saveDisabled =
        isEditingLinked ||
        (linkedRefId === null &&
            (draft.name.trim() === "" || draft.legIds.length === 0 || !areAllSelectedLegsResolved));

    const moveLeg = (index: number, direction: -1 | 1) => {
        setDraft((current) => {
            const target = index + direction;
            if (target < 0 || target >= current.legIds.length) return current;

            const next = [...current.legIds];
            [next[index], next[target]] = [next[target], next[index]];
            return { ...current, legIds: next };
        });
    };

    const removeLeg = (index: number) => {
        setDraft((current) => ({ ...current, legIds: current.legIds.filter((_id, idx) => idx !== index) }));
    };

    const handleSave = async () => {
        if (isEditMode && editingCircuit?.id !== editingCircuitId) return;
        if (linkedRefId === null && !isEditingLinked && !areAllSelectedLegsResolved) return;

        const orgIri = `/api/organizations/${organizationId}` as IRI;
        const existingCircuitLegIdsByTransportLegIri = new Map<string, string[]>();
        for (const circuitLeg of editingCircuit?.circuitLegs ?? []) {
            const transportLegIri =
                typeof circuitLeg.transportLeg === "string" ?
                    circuitLeg.transportLeg
                :   getLegIri(circuitLeg.transportLeg);
            if (!circuitLeg["@id"]) continue;
            existingCircuitLegIdsByTransportLegIri.set(transportLegIri, [
                ...(existingCircuitLegIdsByTransportLegIri.get(transportLegIri) ?? []),
                circuitLeg["@id"],
            ]);
        }

        const payload: TransportCircuitWrite & { id?: number } = {
            organization: orgIri,
            linkMode: editingCircuit?.linkMode ?? (linkedRefId !== null ? "LINKED" : "LOCAL"),
            sourceReferenceCircuitId:
                editingCircuit?.linkMode === "LINKED" ?
                    (editingCircuit.sourceReferenceCircuitId ?? null)
                :   (linkedRefId ?? null),
            name: draft.name || null,
            circuitLegs:
                linkedRefId !== null && !editingCircuit ?
                    []
                :   selectedLegs.map((leg, orderIndex) => {
                        const transportLeg = getLegIri(leg);
                        const existingIds = existingCircuitLegIdsByTransportLegIri.get(transportLeg) ?? [];
                        const existingId = existingIds.shift();

                        return {
                            ...(existingId ? { "@id": existingId } : {}),
                            transportLeg,
                            orderIndex,
                        };
                    }),
            id: editingCircuit?.id,
        };

        try {
            await upsertCircuit.mutateAsync(payload);
            navigate(URLHandler.transport.list(organizationId));
        } catch (error) {
            if (isTransportMutationContractError(error)) {
                setSnackbarProps({
                    message: t(
                        "transport.circuits.contract_error_after_save",
                        "The transport itinerary was saved, but its response is invalid. The list was refreshed.",
                    ),
                    type: "error",
                });
                navigate(URLHandler.transport.list(organizationId));
                return;
            }

            setSnackbarProps({
                message: t("transport.circuits.save_error", "Failed to save the transport circuit. Please try again."),
                type: "error",
            });
        }
    };

    const legCatalogStatus =
        isTransportLegsLoading ?
            <Stack alignItems="center" justifyContent="center" minHeight={theme.spacing(14)}>
                {appearance === "legacy" ?
                    <CircularProgress />
                :   <FoodsLoadingIndicator label={t("common.loading", "Loading...")} />}
            </Stack>
        : isTransportLegsError ?
            <RemoteErrorAlert
                message={t(
                    "generic.unexpected_error",
                    "An unexpected error occurred. Please try again later. You can contact our support team.",
                )}
                status="server-error"
            />
        :   null;

    return (
        <TransportCreatePageShell
            companyId={organizationId}
            title={
                isEditMode ?
                    t("transport.circuits.edit_itinerary", "Edit itinerary")
                :   t("transport.circuits.create_itinerary", "New itinerary")
            }
            saveDisabled={saveDisabled}
            saveLoading={upsertCircuit.isLoading}
            onSave={handleSave}
        >
            <TransportFormTabs
                ariaLabel={
                    isEditMode ?
                        t("transport.circuits.edit_itinerary", "Edit itinerary")
                    :   t("transport.circuits.create_itinerary", "New itinerary")
                }
                idPrefix="transport-itinerary"
                informationLabel={t("transport.tabs.information", "Information")}
                onChange={setSelectedTab}
                scoresLabel={t("transport.tabs.scores", "Scores")}
                value={selectedTab}
            />
            {isEditMode && isEditingCircuitError && editingCircuit !== null && (
                <RemoteErrorAlert
                    message={t(
                        "transport.circuits.refresh_error",
                        "Unable to refresh transport itinerary. Showing saved data.",
                    )}
                    status="server-error"
                />
            )}
            {selectedTab === "scores" && (
                <TransportFormTabPanel idPrefix="transport-itinerary" value="scores">
                    <TransportScoresPanel kind="circuit" transportId={editingCircuit?.id} />
                </TransportFormTabPanel>
            )}
            {selectedTab === "form" && (
                <TransportFormTabPanel idPrefix="transport-itinerary" value="form">
                    <TransportFormMapLayout>
                        <Stack gap={2}>
                            {!isEditMode && (
                                <TransportCreateSection
                                    title={t("transport.create.sections.reference_transport", "Reference transport")}
                                    progress={{ current: 1, total: creationIdentitySectionTotal }}
                                    description={t(
                                        "transport.create.reference_transport_help",
                                        "If you want to use generic data, you can choose a reference on the following list. If you prefer to use your own specific data, don’t select any reference.",
                                    )}
                                >
                                    {appearance === "legacy" ?
                                        <Autocomplete
                                            options={transportReferences}
                                            getOptionLabel={(option) => option.name ?? option.code}
                                            value={
                                                transportReferences.find((reference) => reference.id === linkedRefId) ??
                                                null
                                            }
                                            onChange={(_event, value) => {
                                                setLinkedRefId(value?.id ?? null);
                                                if (value === null) {
                                                    setDraft(blankDraft);
                                                    return;
                                                }

                                                setDraft((current) => ({
                                                    ...current,
                                                    name: value.name ?? current.name,
                                                    legIds: [],
                                                }));
                                            }}
                                            renderInput={(params) => (
                                                <TextField
                                                    {...params}
                                                    label={t(
                                                        "transport.circuits.from_reference",
                                                        "Link to a reference circuit",
                                                    )}
                                                    placeholder={t(
                                                        "transport.create.select_reference",
                                                        "Select a reference",
                                                    )}
                                                    sx={transportCreateTextFieldSx(theme)}
                                                />
                                            )}
                                        />
                                    :   <FoodsSelectField
                                            appearance="product-v2"
                                            options={transportReferences}
                                            getId={(option) => option.id}
                                            getName={(option) => option.name ?? option.code}
                                            selectedOption={
                                                transportReferences.find((reference) => reference.id === linkedRefId) ??
                                                null
                                            }
                                            onChange={(value) => {
                                                setLinkedRefId(value?.id ?? null);
                                                if (value === null) {
                                                    setDraft(blankDraft);
                                                    return;
                                                }

                                                setDraft((current) => ({
                                                    ...current,
                                                    name: value.name ?? current.name,
                                                    legIds: [],
                                                }));
                                            }}
                                            label={t(
                                                "transport.circuits.from_reference",
                                                "Link to a reference circuit",
                                            )}
                                            placeholder={t("transport.create.select_reference", "Select a reference")}
                                            searchPlaceholder={t("component.select.searchplaceholder", "Search…")}
                                            emptyOptionsText={t("component.select.nooptions", "No option available")}
                                            noResultText={t("component.select.noresultfound", "No results found")}
                                            noValue={t("component.select.unselectvalue", "Don't select anything")}
                                        />
                                    }
                                </TransportCreateSection>
                            )}

                            <TransportCreateSection
                                title={t("transport.create.sections.itinerary_name", "Name")}
                                progress={
                                    appearance === "legacy" && isEditMode ? undefined
                                    : isEditMode ?
                                        { current: 1, total: editSectionTotal }
                                    :   { current: 2, total: creationIdentitySectionTotal }
                                }
                            >
                                {appearance === "legacy" ?
                                    <TextField
                                        label={t("transport.create.itinerary_name_field", "Itinerary name")}
                                        value={draft.name}
                                        onChange={(event) =>
                                            setDraft((current) => ({ ...current, name: event.target.value }))
                                        }
                                        disabled={isReferenceLocked}
                                        fullWidth
                                        sx={transportCreateTextFieldSx(theme)}
                                    />
                                :   <FoodsTextField
                                        label={t("transport.create.itinerary_name_field", "Itinerary name")}
                                        value={draft.name}
                                        onChange={(name) => setDraft((current) => ({ ...current, name }))}
                                        disabled={isReferenceLocked}
                                    />
                                }
                            </TransportCreateSection>

                            <TransportCircuitLegsSection
                                draftLegCount={draft.legIds.length}
                                editSectionTotal={editSectionTotal}
                                isEditMode={isEditMode}
                                isReferenceLocked={isReferenceLocked}
                                isTransportLegCatalogUnavailable={isTransportLegCatalogUnavailable}
                                legCatalogStatus={legCatalogStatus}
                                selectedLegEntries={selectedLegEntries}
                                transportModes={transportModes}
                                onAddLeg={() => setShowAddLeg(true)}
                                onMoveLeg={moveLeg}
                                onRemoveLeg={removeLeg}
                            />
                        </Stack>
                        <TransportFormMapPanel title={t("transport.create.map_title", "Transport map")}>
                            {appearance === "legacy" ?
                                <Box
                                    sx={{
                                        borderRadius: theme.spacing(1.5),
                                        overflow: "hidden",
                                        backgroundColor: theme.custom.grey[300],
                                    }}
                                >
                                    {previewCircuit ?
                                        <Suspense
                                            fallback={
                                                <Box
                                                    display="flex"
                                                    justifyContent="center"
                                                    alignItems="center"
                                                    minHeight={420}
                                                >
                                                    <CircularProgress />
                                                </Box>
                                            }
                                        >
                                            <LazyTransportCircuitMap circuit={previewCircuit} heightPx={420} />
                                        </Suspense>
                                    :   <Stack alignItems="center" justifyContent="center" minHeight={420} p={3}>
                                            <MuiTypography variant="body2" sx={{ color: theme.custom.grey[2000] }}>
                                                {t(
                                                    "transport.create.itinerary_map_empty",
                                                    "Add legs to preview the itinerary on the map.",
                                                )}
                                            </MuiTypography>
                                        </Stack>
                                    }
                                </Box>
                            :   <Box sx={{ minWidth: 0 }}>
                                    {previewCircuit ?
                                        <Suspense
                                            fallback={
                                                <Box
                                                    display="flex"
                                                    justifyContent="center"
                                                    alignItems="center"
                                                    sx={{ aspectRatio: transportMapPreviewAspectRatio }}
                                                >
                                                    <FoodsLoadingIndicator label={t("common.loading", "Loading...")} />
                                                </Box>
                                            }
                                        >
                                            <LazyTransportCircuitMap
                                                circuit={previewCircuit}
                                                aspectRatio={transportMapPreviewAspectRatio}
                                            />
                                        </Suspense>
                                    :   <Suspense
                                            fallback={
                                                <Box
                                                    display="flex"
                                                    justifyContent="center"
                                                    alignItems="center"
                                                    sx={{ aspectRatio: transportMapPreviewAspectRatio }}
                                                >
                                                    <FoodsLoadingIndicator label={t("common.loading", "Loading...")} />
                                                </Box>
                                            }
                                        >
                                            <LazyTransportEmptyMapPreview
                                                accessibleLabel={t(
                                                    "transport.create.itinerary_map_empty",
                                                    "Add legs to preview the itinerary on the map.",
                                                )}
                                                aspectRatio={transportMapPreviewAspectRatio}
                                            />
                                        </Suspense>
                                    }
                                </Box>
                            }
                        </TransportFormMapPanel>
                    </TransportFormMapLayout>

                    <TransportAddLegModal
                        appearance={appearance}
                        open={showAddLeg && !isTransportLegCatalogUnavailable}
                        legs={transportLegs}
                        onClose={() => setShowAddLeg(false)}
                        onAdd={(leg) => setDraft((current) => ({ ...current, legIds: [...current.legIds, leg.id] }))}
                    />
                </TransportFormTabPanel>
            )}
        </TransportCreatePageShell>
    );
};

export default TransportCircuitCreatePage;
